import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import {
  DRILLDOWN_KEY_TO_ENTITY_TYPE,
  DrillDownCategory,
  INSIGHT_TO_DRILLDOWN_CATEGORY,
  InsightCatalogue,
} from 'types/store/normalized/insights/InsightCatalogue';
import { DrilldownJsonApiResponse } from 'api/normalized/insightDrilldown/InsightDrilldownApi';
import { SLICE_STATUS } from 'utils/constants';
import drilldownActions from 'state/normalized/insights/drilldownActions';
import { insightDrilldownFiltersSelectors } from '../insightDrilldownFilters/insightDrilldownFiltersSlice';

/* ============================== STATE SETUP =============================== */
interface DrilldownMetadata {
  id: InsightCatalogue;
  entityType: DrillDownCategory;
  entityIds: string[];
  meta?: DrilldownJsonApiResponse['meta'];
  links?: DrilldownJsonApiResponse['links'];
}

interface MetadataState {
  statusById: Partial<Record<InsightCatalogue, Status>>;
  activeDrilldownId?: InsightCatalogue;
  activeDrilldownKey?: string;
}

const initialState: MetadataState = {
  statusById: {},
  activeDrilldownId: undefined,
};

const insightDrilldownMetadataAdapter =
  createEntityAdapter<DrilldownMetadata>();

/* ============================== REDUX THUNK =============================== */
export const getActiveDrilldownData = createAsyncThunk<
  DrilldownMetadata,
  string,
  ThunkApi
>(
  'normalized/insightDrilldownMetadata/getDrilldownData',
  async (url, { extra: { getInsightDrilldownApi }, dispatch, getState }) => {
    const insightId = selectActiveDrilldownId(getState())!;
    const entityType = selectActiveDrilldownEntityType(getState())!;
    const filters =
      insightDrilldownFiltersSelectors.selectInsightDrilldownFiltersForQuery(
        getState()
      );

    const response = await getInsightDrilldownApi().getDrilldownData(
      url,
      filters
    );
    const entityIds = response.data.map((item) => item.id);

    dispatch(drilldownActions[entityType].addToStore(response.data));

    return {
      id: insightId,
      entityType,
      entityIds,
      meta: response.meta,
      links: response.links,
    };
  },
  {
    condition: (_url, { getState }: { getState: () => RootState }) => {
      const insightId = selectActiveDrilldownId(getState());
      const entityType = selectActiveDrilldownEntityType(getState());
      return !!insightId && !!entityType;
    },
  }
);

/* ================================= REDUCER ================================ */
const insightDrilldownMetadataSlice = createSlice({
  name: 'normalized/insightDrilldownMetadata',
  initialState: insightDrilldownMetadataAdapter.getInitialState(initialState),
  reducers: {
    setActiveDrilldownId: (
      state,
      action: PayloadAction<InsightCatalogue | undefined>
    ) => {
      state.activeDrilldownId = action.payload;
    },
    setActiveDrilldownKey: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.activeDrilldownKey = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActiveDrilldownData.fulfilled, (state, action) => {
        insightDrilldownMetadataAdapter.setOne(state, action.payload);
        state.statusById[action.payload.id] = SLICE_STATUS.SUCCESS;
      })
      .addCase(getActiveDrilldownData.rejected, (state) => {
        const insightId = state.activeDrilldownId!;
        state.statusById[insightId] = SLICE_STATUS.FAILED;
      })
      .addCase(getActiveDrilldownData.pending, (state) => {
        const insightId = state.activeDrilldownId!;
        state.statusById[insightId] = SLICE_STATUS.LOADING;
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors =
  insightDrilldownMetadataAdapter.getSelectors<RootState>(
    (state) => state.normalized.insightDrilldownMetadata
  );

const selectActiveDrilldownId = (
  state: RootState
): InsightCatalogue | undefined => {
  return state.normalized.insightDrilldownMetadata.activeDrilldownId;
};

const selectActiveDrilldown = (
  state: RootState
): DrilldownMetadata | undefined => {
  const insightId = state.normalized.insightDrilldownMetadata.activeDrilldownId;
  if (insightId) {
    return defaultSelectors.selectById(state, insightId);
  }
};

const selectActiveDrilldownKey = (state: RootState): string | undefined => {
  return state.normalized.insightDrilldownMetadata.activeDrilldownKey;
};

const selectIsActiveDrilldownLoading = (state: RootState): boolean => {
  const insightId = selectActiveDrilldownId(state);
  return (
    !!insightId &&
    state.normalized.insightDrilldownMetadata.statusById[insightId] ===
      SLICE_STATUS.LOADING
  );
};

const selectActiveDrilldownEntityType = createSelector(
  selectActiveDrilldownId,
  selectActiveDrilldownKey,
  (activeDrilldownId, activeDrilldownKey) => {
    if (
      activeDrilldownKey &&
      DRILLDOWN_KEY_TO_ENTITY_TYPE[activeDrilldownKey]
    ) {
      return DRILLDOWN_KEY_TO_ENTITY_TYPE[activeDrilldownKey];
    }
    if (activeDrilldownId) {
      return INSIGHT_TO_DRILLDOWN_CATEGORY[activeDrilldownId];
    }
    return undefined;
  }
);

/* ================================ EXPORTS ================================= */
export const insightDrilldownMetadataSelectors = {
  ...defaultSelectors,
  selectActiveDrilldownId,
  selectActiveDrilldown,
  selectActiveDrilldownEntityType,
  selectIsActiveDrilldownLoading,
};
export const { setActiveDrilldownId, setActiveDrilldownKey } =
  insightDrilldownMetadataSlice.actions;
export default insightDrilldownMetadataSlice.reducer;
